import type { Auth0ContextInterface } from '@auth0/auth0-react';

export type Auth0Like = Pick<
    Auth0ContextInterface,
    'getAccessTokenSilently' | 'loginWithRedirect'
>;

/**
 * Error codes @auth0/auth0-spa-js surfaces when silent token acquisition can
 * only be fixed by an interactive login.
 *
 * Single source of truth: authExpiryGuard imports this instead of keeping its
 * own copy. The two lists having drifted apart is what caused PLATAFORMA-8 —
 * the guard already knew `invalid_grant` / `missing_refresh_token` mean "dead
 * session", but safeGetAccessToken only recognised `login_required`, so every
 * callsite rethrew the SDK error into its catch block (Sentry warning + a
 * misleading toast) instead of sending the user to log in again.
 */
const UNRECOVERABLE_CODES = new Set([
    'missing_refresh_token', // ITP/private mode evicted the refresh token
    'invalid_grant', // refresh token expired, revoked or rotated-out
    'login_required', // Auth0 session is gone
    'interaction_required',
    'consent_required',
]);

export function isUnrecoverableTokenError(e: unknown): boolean {
    if (!e || typeof e !== 'object') return false;
    const code = (e as { error?: unknown }).error;
    return typeof code === 'string' && UNRECOVERABLE_CODES.has(code);
}

/**
 * Some SDK paths reject with only the human-readable message and no `error`
 * code, so keep matching "Login required" by message as well.
 */
const isDeadSessionError = (e: unknown): boolean => {
    if (isUnrecoverableTokenError(e)) return true;
    if (!e || typeof e !== 'object') return false;
    const message = (e as { message?: unknown }).message;
    return typeof message === 'string' && /^login.required$/i.test(message);
};

// Only ONE interactive redirect may be in flight. safeGetAccessToken is called
// from ~30 callsites, several of them on mount of the same page, so a dead
// session would otherwise fire a burst of concurrent loginWithRedirect calls.
// Reset on failure so a redirect that never happened can be retried.
let redirectInFlight: Promise<unknown> | null = null;

/** Test-only: clear the in-flight redirect between cases. */
export function __resetLoginRedirectForTests(): void {
    redirectInFlight = null;
}

export async function safeGetAccessToken(auth0: Auth0Like): Promise<string | null> {
    try {
        return await auth0.getAccessTokenSilently();
    } catch (e) {
        if (!isDeadSessionError(e)) throw e;
        if (!redirectInFlight) {
            const returnTo = window.location.pathname + window.location.search;
            redirectInFlight = Promise.resolve(
                auth0.loginWithRedirect({ appState: { returnTo } })
            ).catch((redirectError) => {
                redirectInFlight = null;
                throw redirectError;
            });
        }
        await redirectInFlight;
        return null;
    }
}
